import React from 'react';
import cn from 'classnames';
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  TransitionChild,
} from '@odin-labs/components';
import { AuthContext } from 'auth';
import { generatePath } from 'utils/generatePath';
import { LockIcon, TimesIcon } from 'components/icons';
import { SidebarProps } from './types';

export function MobileSidebar(props: SidebarProps): React.ReactElement {
  const {
    isSidebarOpen,
    closeSidebar,
    items,
    getItemProps,
    classes,
    header,
    footer,
  } = props;
  const { currentUser: user } = React.useContext(AuthContext);

  return (
    <Dialog
      as="div"
      className="sidebar-mobile odin-fixed odin-inset-0 odin-flex odin-z-40 md:odin-hidden"
      open={isSidebarOpen}
      onClose={closeSidebar}
    >
      <DialogBackdrop
        transition
        className="odin-fixed odin-inset-0 odin-bg-gray-900/60 odin-transition-opacity odin-ease-linear odin-duration-300 data-[closed]:odin-opacity-0"
      />
      <DialogPanel
        transition
        className={cn(
          classes.background,
          'odin-relative odin-flex odin-flex-col odin-max-w-xs',
          'odin-transition odin-ease-in-out odin-duration-300 odin-transform data-[closed]:odin--translate-x-full',
        )}
      >
        <TransitionChild>
          <div className="odin-absolute odin-top-0 odin-right-0 odin--mr-12 odin-pt-2 odin-ease-in-out odin-duration-300 data-[closed]:odin-opacity-0">
            <button
              className={cn(
                'odin-ml-1',
                'odin-flex',
                'odin-items-center',
                'odin-justify-center',
                'odin-h-10',
                'odin-w-10',
                'odin-rounded-full',
                'odin-border',
                'odin-border-white',
              )}
              onClick={closeSidebar}
            >
              <span className="odin-sr-only">Close sidebar</span>
              <TimesIcon
                className="odin-h-6 odin-w-6 odin-text-white"
                aria-hidden="true"
              />
            </button>
          </div>
        </TransitionChild>
        <div className="odin-flex-1 odin-h-0 odin-pt-5 odin-pb-4 odin-px-6 odin-overflow-y-auto">
          <div className={classes.header('mobile')}>{header}</div>
          <nav className="odin-mt-5 odin-space-y-1">
            {items.map((item): React.ReactElement => {
              if (!item.acl || user.hasAnyRole(item.acl)) {
                const { isLocked, isActive, openInNewTabProps } =
                  getItemProps(item);
                const { link: Link, icon: Icon, status } = item;
                const {
                  text: statusText,
                  color: statusColor,
                  icon: StatusIcon,
                } = status ?? {};

                return (
                  <Link
                    key={item.path}
                    to={generatePath(item.path, {})}
                    onClick={closeSidebar}
                    className={cn(
                      classes.item(isActive, 'mobile'),
                      'odin-p-3 odin-text-base',
                    )}
                    {...openInNewTabProps}
                  >
                    <div className="odin-flex odin-items-center odin-gap-x-3">
                      {Icon && (
                        <Icon
                          className={cn(
                            classes.icon(isActive, 'mobile'),
                            'odin-text-lg odin-h-5 odin-w-6 odin-shrink-0',
                          )}
                        />
                      )}
                      <span className="odin-whitespace-nowrap">
                        {item.text}
                      </span>
                      {isLocked && <LockIcon />}
                    </div>
                    {statusText && (
                      <div
                        className={cn(
                          statusColor,
                          'odin-flex odin-items-center odin-mt-2 odin-gap-x-2',
                        )}
                      >
                        {StatusIcon && <StatusIcon />} {statusText}
                      </div>
                    )}
                  </Link>
                );
              }
              return null;
            })}
          </nav>
        </div>
        <div className={classes.footer('mobile')}>{footer}</div>
      </DialogPanel>
    </Dialog>
  );
}
